import { useState, useEffect, useMemo } from 'react';
import { MilestoneMap1, HierarchyZoomLink } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { updateMilestoneMap } from '../../store/map3PendingSlice';
import { useParams, useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { fetchMilestoneMap } from '../../store/map3DataSlice';
import { getPermissionsForRole, resolveRole } from '@mm-shared/authz';
import { MapTimeZoneSetting, collectMapStatuses } from './MapTimeZoneSetting';

export function Map3Settings(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const { userName, mapShortId } = useParams();
  const navigate = useNavigate();
  const [milestoneMap, setMilestoneMap] = useState<MilestoneMap1 | null>(null);
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const isAlphaAdmin = userProfile?.accountType === 'alpha_admin';

  // Fetch milestone map when component mounts or mapShortId changes
  useEffect(() => {
    async function fetchMap() {
      if (mapShortId) {
        try {
          const map = await dispatch(fetchMilestoneMap(mapShortId)).unwrap();
          setMilestoneMap(map);
        } catch (error) {
          console.error('Error fetching milestone map:', error);
        }
      }
    }
    fetchMap();
  }, [mapShortId, dispatch]);

  // Initialize form data with milestone map values
  const [formData, setFormData] = useState<Partial<MilestoneMap1>>({
    title: '',
    interval: 'weeks',
    intervalInDays: 7,
    startDate: '',
    endDate: '',
  });

  // Update form data when milestone map changes
  useEffect(() => {
    if (milestoneMap) {
      setFormData({
        title: milestoneMap.title,
        interval: milestoneMap.interval,
        intervalInDays: milestoneMap.intervalInDays,
        startDate: milestoneMap.startDate,
        endDate: milestoneMap.endDate,
      });
    }
  }, [milestoneMap]);

  const [helpVideoInputs, setHelpVideoInputs] = useState<
    Array<{ url: string; title: string; description: string }>
  >([{ url: '', title: '', description: '' }]);
  const [videoErrors, setVideoErrors] = useState<
    Array<{
      url?: string;
      title?: string;
      description?: string;
    }>
  >([]);

  // Update help video inputs when milestone map changes
  useEffect(() => {
    if (milestoneMap?.helpVideos && milestoneMap.helpVideos.length > 0) {
      setHelpVideoInputs(milestoneMap.helpVideos);
    }
  }, [milestoneMap]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, title: e.target.value }));
  };

  const handleHelpVideosSave = () => {
    // Reset errors
    const newErrors: Array<{
      url?: string;
      title?: string;
      description?: string;
    }> = helpVideoInputs.map(() => ({}));
    let hasErrors = false;

    // Validate all inputs
    helpVideoInputs.forEach((video, index) => {
      // Skip validation if all fields are empty
      if (!video.url.trim() && !video.title.trim() && !video.description.trim()) {
        return;
      }

      // Validate required fields
      if (!video.title.trim()) {
        newErrors[index].title = 'Title is required';
        hasErrors = true;
      }
      if (!video.url.trim()) {
        newErrors[index].url = 'URL is required';
        hasErrors = true;
      } else {
        try {
          const url = new URL(video.url);
          const isValidVideoUrl =
            url.hostname.includes('youtube.com') ||
            url.hostname.includes('youtu.be') ||
            url.hostname.includes('vimeo.com') ||
            url.hostname.includes('loom.com');

          if (!isValidVideoUrl) {
            newErrors[index].url = 'Only YouTube, Vimeo, and Loom URLs are allowed';
            hasErrors = true;
          }
        } catch {
          newErrors[index].url = 'Please enter a valid URL';
          hasErrors = true;
        }
      }
      if (!video.description.trim()) {
        newErrors[index].description = 'Description is required';
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setVideoErrors(newErrors);
      return;
    }

    // Filter out empty entries and save
    const validVideos = helpVideoInputs.filter(
      (video) => video.url.trim() && video.title.trim() && video.description.trim(),
    );

    if (milestoneMap?.uuid) {
      dispatch(
        updateMilestoneMap({
          uuid: milestoneMap.uuid,
          updates: {
            title: formData.title,
            helpVideos: validVideos,
          },
        }),
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Filter out empty help video entries
    const validVideos = helpVideoInputs.filter(
      (video) => video.url.trim() && video.title.trim() && video.description.trim(),
    );

    const updates = {
      title: formData.title,
      interval: formData.interval,
      intervalInDays: formData.intervalInDays,
      startDate: formData.startDate,
      endDate: formData.endDate,
      helpVideos: validVideos,
      featureFlags: featureFlags,
    };

    if (milestoneMap?.uuid) {
      dispatch(
        updateMilestoneMap({
          uuid: milestoneMap.uuid,
          updates: updates,
        }),
      );
      navigate(`/${userName}/maps/${mapShortId}`);
    }
  };

  const [isHelpVideosExpanded, setIsHelpVideosExpanded] = useState(false);
  const [isExperimentalExpanded, setIsExperimentalExpanded] = useState(false);
  const [isHierarchyZoomExpanded, setIsHierarchyZoomExpanded] = useState(false);
  const [zoomLinks, setZoomLinks] = useState<HierarchyZoomLink[]>([]);
  const [featureFlags, setFeatureFlags] = useState<NonNullable<MilestoneMap1['featureFlags']>>({
    enableTreeView: false,
    enableRelatives: false,
    enablePlaylists: false,
    enableCrosshairs: false,
    enableAddExistingObjectives: false,
    enableHierarchyZoom: false,
    enableDescendants: false,
    enableImplementationPlans: false,
  });

  // Sync zoomLinks state when milestoneMap loads
  useEffect(() => {
    if (milestoneMap?.hierarchyZoomLinks) {
      setZoomLinks(milestoneMap.hierarchyZoomLinks);
    }
  }, [milestoneMap]);

  // Sync featureFlags state when milestoneMap loads
  useEffect(() => {
    if (milestoneMap?.featureFlags) {
      setFeatureFlags(milestoneMap.featureFlags);
    }
  }, [milestoneMap]);

  // Get all milestone maps user has access to (for dropdown options)
  // Combine myMilestoneMaps + sharedMilestoneMaps from userProfile — same pattern as Map3Workspace.
  // state.map3Data.milestoneMaps only contains the current map (subscribed per-map), so it was
  // always empty for other maps and left the dropdown with no options.
  const allMilestoneMaps = [
    ...(userProfile?.myMilestoneMaps || []),
    ...(userProfile?.sharedMilestoneMaps || []),
  ];

  // Get objectives for current map from Redux
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  // Time zone setting (owner-only). Inference reads only this map's objectives' statuses.
  const canManageSettings = getPermissionsForRole(
    resolveRole(milestoneMap, userProfile?.uid),
  ).canManageSettings;
  const allStatuses = useMemo(
    () => collectMapStatuses(milestoneMap, allObjectives),
    [milestoneMap, allObjectives],
  );
  // Time zone writes go through the Redux queue immediately, independent of the form's Save.
  // Clearing writes '' (readers treat a falsy timeZone as unset), because the queue cannot carry
  // Firestore's deleteField() sentinel.
  const handleTimeZoneChange = (timeZone: string | null) => {
    if (!milestoneMap?.uuid) return;
    dispatch(updateMilestoneMap({ uuid: milestoneMap.uuid, updates: { timeZone: timeZone ?? '' } }));
  };
  const parentObjectives = milestoneMap?.parentObjectiveUuids
    ?.map((uuid) => allObjectives.find((o) => o.uuid === uuid))
    .filter(Boolean) || [];
  const childObjectives = milestoneMap?.objectiveUuids
    ?.map((uuid) => allObjectives.find((o) => o.uuid === uuid))
    .filter(Boolean) || [];

  // Get current zoom link for an objective
  const getZoomLink = (objectiveUuid: string): HierarchyZoomLink | undefined => {
    return zoomLinks.find((link) => link.objectiveUuid === objectiveUuid);
  };

  // Update a zoom link
  const handleZoomLinkChange = (
    objectiveUuid: string,
    direction: 'in' | 'out',
    mapShortId: string | null,
  ) => {
    setZoomLinks((prev) => {
      const existing = prev.find((l) => l.objectiveUuid === objectiveUuid);
      if (existing) {
        return prev.map((l) =>
          l.objectiveUuid === objectiveUuid
            ? {
                ...l,
                [direction === 'in' ? 'zoomInMapShortId' : 'zoomOutMapShortId']:
                  mapShortId || undefined,
              }
            : l,
        );
      } else {
        return [
          ...prev,
          {
            objectiveUuid,
            [direction === 'in' ? 'zoomInMapShortId' : 'zoomOutMapShortId']:
              mapShortId || undefined,
          },
        ];
      }
    });
  };

  // Handle feature flag changes
  const handleFeatureFlagChange = (flag: keyof NonNullable<MilestoneMap1['featureFlags']>, value: boolean) => {
    setFeatureFlags(prev => ({ ...prev, [flag]: value }));
  };

  // Save zoom links
  const handleSaveZoomLinks = () => {
    if (milestoneMap?.uuid) {
      dispatch(
        updateMilestoneMap({
          uuid: milestoneMap.uuid,
          updates: {
            hierarchyZoomLinks: zoomLinks.filter(
              (l) => l.zoomInMapShortId || l.zoomOutMapShortId,
            ),
          },
        }),
      );
    }
  };

  const handleVideoEntryBlur = (event: React.FocusEvent<HTMLDivElement>, index: number) => {
    // Check if the new focus target is outside this video entry
    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget as Node;

    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      // User has moved focus outside this video entry, trigger save
      const video = helpVideoInputs[index];
      if (video.url.trim() || video.title.trim() || video.description.trim()) {
        handleHelpVideosSave();
      }
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, startDate: e.target.value }));
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, endDate: e.target.value }));
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-xl font-semibold mb-4">Map Settings</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-4 items-center">
          <label htmlFor="title" className="text-sm font-medium">
            Title
          </label>
          <div className="col-span-2">
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleTitleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 items-center">
          <label htmlFor="interval" className="text-sm font-medium">
            Interval
          </label>
          <div className="col-span-2">
            <select
              id="interval"
              name="interval"
              value={formData.interval}
              disabled
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="days">Daily</option>
              <option value="weeks">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="months">Monthly</option>
              <option value="quarters">Quarterly</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 items-center">
          <label htmlFor="startDate" className="text-sm font-medium">
            Start Date
          </label>
          <div className="col-span-2">
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleStartDateChange}
              disabled={!isAlphaAdmin}
              className={`w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                !isAlphaAdmin ? 'bg-gray-100' : ''
              }`}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 items-center">
          <label htmlFor="endDate" className="text-sm font-medium">
            End Date
          </label>
          <div className="col-span-2">
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleEndDateChange}
              disabled={!isAlphaAdmin}
              className={`w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                !isAlphaAdmin ? 'bg-gray-100' : ''
              }`}
            />
          </div>
        </div>

        {/* Help Videos Section */}
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setIsHelpVideosExpanded(!isHelpVideosExpanded)}
            className="flex items-center space-x-2 w-full text-left"
          >
            {isHelpVideosExpanded ? (
              <ChevronDownIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
            <h3 className="text-lg font-semibold">Help Videos</h3>
          </button>

          {isHelpVideosExpanded && (
            <div className="space-y-4 mt-4">
              {helpVideoInputs.map((video, index) => (
                <div
                  key={index}
                  className="space-y-2 p-4 border rounded-md"
                  onBlur={(e) => handleVideoEntryBlur(e, index)}
                  tabIndex={-1} // Makes the div focusable but not in tab order
                >
                  <div className="space-y-1">
                    <input
                      type="text"
                      placeholder="Video Title"
                      value={video.title}
                      onChange={(e) => {
                        const newVideos = helpVideoInputs.map((v, i) =>
                          i === index ? { ...v, title: e.target.value } : v,
                        );
                        setHelpVideoInputs(newVideos);
                        if (videoErrors[index]?.title) {
                          const newErrors = [...videoErrors];
                          newErrors[index] = { ...newErrors[index], title: undefined };
                          setVideoErrors(newErrors);
                        }
                      }}
                      className={`w-full px-2 py-1 rounded border ${
                        videoErrors[index]?.title ? 'border-red-500' : 'border-gray-300'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    />
                    {videoErrors[index]?.title && (
                      <div className="text-red-500 text-sm">{videoErrors[index].title}</div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <input
                      type="text"
                      placeholder="Video URL"
                      value={video.url}
                      onChange={(e) => {
                        const newVideos = helpVideoInputs.map((v, i) =>
                          i === index ? { ...v, url: e.target.value } : v,
                        );
                        setHelpVideoInputs(newVideos);
                        if (videoErrors[index]?.url) {
                          const newErrors = [...videoErrors];
                          newErrors[index] = { ...newErrors[index], url: undefined };
                          setVideoErrors(newErrors);
                        }
                      }}
                      className={`w-full px-2 py-1 rounded border ${
                        videoErrors[index]?.url ? 'border-red-500' : 'border-gray-300'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    />
                    {videoErrors[index]?.url && (
                      <div className="text-red-500 text-sm">{videoErrors[index].url}</div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <textarea
                      placeholder="Video Description"
                      value={video.description}
                      onChange={(e) => {
                        const newVideos = helpVideoInputs.map((v, i) =>
                          i === index ? { ...v, description: e.target.value } : v,
                        );
                        setHelpVideoInputs(newVideos);
                        if (videoErrors[index]?.description) {
                          const newErrors = [...videoErrors];
                          newErrors[index] = { ...newErrors[index], description: undefined };
                          setVideoErrors(newErrors);
                        }
                      }}
                      className={`w-full px-2 py-1 rounded border ${
                        videoErrors[index]?.description ? 'border-red-500' : 'border-gray-300'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                      rows={3}
                    />
                    {videoErrors[index]?.description && (
                      <div className="text-red-500 text-sm">{videoErrors[index].description}</div>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  setHelpVideoInputs([...helpVideoInputs, { url: '', title: '', description: '' }])
                }
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                + Add another video
              </button>
            </div>
          )}
        </div>

        {/* Experimental Features Section */}
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setIsExperimentalExpanded(!isExperimentalExpanded)}
            className="flex items-center space-x-2 w-full text-left"
          >
            {isExperimentalExpanded ? (
              <ChevronDownIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
            <h3 className="text-lg font-semibold">Experimental Features</h3>
          </button>

          {isExperimentalExpanded && (
            <div className="space-y-4 mt-4 pl-7">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableTreeView"
                  name="enableTreeView"
                  className="rounded"
                  checked={featureFlags.enableTreeView || false}
                  onChange={(e) => handleFeatureFlagChange('enableTreeView', e.target.checked)}
                />
                <label htmlFor="enableTreeView">
                  Enable Tree View for accessible keyboard and screen reader navigation
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableRelatives"
                  name="enableRelatives"
                  className="rounded"
                  checked={featureFlags.enableRelatives || false}
                  onChange={(e) => handleFeatureFlagChange('enableRelatives', e.target.checked)}
                />
                <label htmlFor="enableRelatives">
                  Enable Relatives View to visualize the relationship between maps
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enablePlaylists"
                  name="enablePlaylists"
                  className="rounded"
                  checked={featureFlags.enablePlaylists || false}
                  onChange={(e) => handleFeatureFlagChange('enablePlaylists', e.target.checked)}
                />
                <label htmlFor="enablePlaylists">
                  Enable Playlists View to watch all the videos in a row or column
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableCrosshairs"
                  name="enableCrosshairs"
                  className="rounded"
                  checked={featureFlags.enableCrosshairs || false}
                  onChange={(e) => handleFeatureFlagChange('enableCrosshairs', e.target.checked)}
                />
                <label htmlFor="enableCrosshairs">
                  Enable Crosshairs to enable row/column highlighting for live meeting facilitation
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableAddExistingObjectives"
                  name="enableAddExistingObjectives"
                  className="rounded"
                  checked={featureFlags.enableAddExistingObjectives || false}
                  onChange={(e) => handleFeatureFlagChange('enableAddExistingObjectives', e.target.checked)}
                />
                <label htmlFor="enableAddExistingObjectives">
                  Enable Add Existing Objectives to quickly add objectives from other maps
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableHierarchyZoom"
                  name="enableHierarchyZoom"
                  className="rounded"
                  checked={featureFlags.enableHierarchyZoom || false}
                  onChange={(e) => handleFeatureFlagChange('enableHierarchyZoom', e.target.checked)}
                />
                <label htmlFor="enableHierarchyZoom">
                  Enable Hierarchy Zoom for navigating between linked parent/child maps
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableDescendants"
                  name="enableDescendants"
                  className="rounded"
                  checked={featureFlags.enableDescendants || false}
                  onChange={(e) => handleFeatureFlagChange('enableDescendants', e.target.checked)}
                />
                <label htmlFor="enableDescendants">
                  Enable Descendants tab to view descendant objectives in the drawer
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enableImplementationPlans"
                  name="enableImplementationPlans"
                  className="rounded"
                  checked={featureFlags.enableImplementationPlans || false}
                  onChange={(e) => handleFeatureFlagChange('enableImplementationPlans', e.target.checked)}
                />
                <label htmlFor="enableImplementationPlans">
                  Enable Implementation Plans view to organize objectives as sticky notes
                </label>
              </div>

            </div>
          )}
        </div>

        {/* POC Hierarchy Zoom Section - only show if feature is enabled */}
        {featureFlags.enableHierarchyZoom && (
          <div className="mt-8">
            <button
              type="button"
              onClick={() => setIsHierarchyZoomExpanded(!isHierarchyZoomExpanded)}
              className="flex items-center space-x-2 w-full text-left"
            >
              {isHierarchyZoomExpanded ? (
                <ChevronDownIcon className="h-5 w-5" />
              ) : (
                <ChevronRightIcon className="h-5 w-5" />
              )}
              <h3 className="text-lg font-semibold">POC Hierarchy Zoom</h3>
            </button>

            {isHierarchyZoomExpanded && (
              <div className="space-y-4 mt-4 pl-7">
                <p className="text-sm text-gray-600">
                  Configure which maps to navigate to when using hierarchy zoom buttons.
                </p>

                {/* Parent Objectives - show zoom OUT */}
                {parentObjectives.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Parent Objectives (Zoom Out)</h4>
                    {parentObjectives.map((obj) => (
                      <div
                        key={obj!.uuid}
                        className="flex items-center gap-4 p-2 border rounded"
                      >
                        <span className="flex-1 truncate text-sm">{obj!.text}</span>
                        <select
                          value={getZoomLink(obj!.uuid)?.zoomOutMapShortId || ''}
                          onChange={(e) =>
                            handleZoomLinkChange(obj!.uuid, 'out', e.target.value || null)
                          }
                          onBlur={handleSaveZoomLinks}
                          className="w-48 text-sm rounded border-gray-300"
                        >
                          <option value="">None</option>
                          {allMilestoneMaps
                            .filter((m) => m.shortId !== milestoneMap?.shortId)
                            .map((map) => (
                              <option key={map.shortId} value={map.shortId}>
                                {map.title}
                              </option>
                            ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {/* Child Objectives - show zoom IN */}
                {childObjectives.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Child Objectives (Zoom In)</h4>
                    {childObjectives.map((obj) => (
                      <div
                        key={obj!.uuid}
                        className="flex items-center gap-4 p-2 border rounded"
                      >
                        <span className="flex-1 truncate text-sm">{obj!.text}</span>
                        <select
                          value={getZoomLink(obj!.uuid)?.zoomInMapShortId || ''}
                          onChange={(e) =>
                            handleZoomLinkChange(obj!.uuid, 'in', e.target.value || null)
                          }
                          onBlur={handleSaveZoomLinks}
                          className="w-48 text-sm rounded border-gray-300"
                        >
                          <option value="">None</option>
                          {allMilestoneMaps
                            .filter((m) => m.shortId !== milestoneMap?.shortId)
                            .map((map) => (
                              <option key={map.shortId} value={map.shortId}>
                                {map.title}
                              </option>
                            ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {parentObjectives.length === 0 && childObjectives.length === 0 && (
                  <p className="text-sm text-gray-500 italic">
                    No objectives found on this map.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
          >
            Save Settings
          </button>
        </div>
      </form>

      {/* Own section, outside the settings <form>: the zone saves on its own button, so the form's
          "Save Settings" and Enter-to-submit can never drop or ignore it (INV-TZ-1). */}
      {milestoneMap && (
        <section className="mt-8 border-t border-gray-200 pt-6" aria-label="Time zone settings">
          <MapTimeZoneSetting
            key={milestoneMap.uuid}
            map={milestoneMap}
            statuses={allStatuses}
            canManage={canManageSettings}
            onChange={handleTimeZoneChange}
          />
        </section>
      )}
    </div>
  );
}
